import { Photo } from "./photo"
import type { Renderer } from "./pipeline/renderer"
import { Adjustments } from "./types"
import { getHealingRenderDataId } from "@/store/editor/healing-utils"

export type AssetRequestType = "ai-mask" | "lut" | "healing-ai"

export interface ArtefactId {
	type: AssetRequestType
	id: string
}

export type ArtefactEntries = Iterable<readonly [ArtefactId, unknown]>

type ArtefactRegistry = Map<AssetRequestType, Map<string, unknown>>

function setRegistryArtefact(registry: ArtefactRegistry, key: ArtefactId, value: unknown) {
	let typedArtefacts = registry.get(key.type)
	if (!typedArtefacts) {
		typedArtefacts = new Map()
		registry.set(key.type, typedArtefacts)
	}
	typedArtefacts.set(key.id, value)
}

function getRegistryArtefact(registry: ArtefactRegistry, key: ArtefactId) {
	return registry.get(key.type)?.get(key.id)
}

export class AssetResolverManager {
	private renderer: Renderer
	private artefacts: ArtefactRegistry = new Map()
	private transientArtefactsByScope = new Map<string, ArtefactRegistry>()

	resolvers: Record<string, AssetResolver> = {}

	constructor(renderer: Renderer, resolvers: AssetResolver[]) {
		this.renderer = renderer

		for (const resolver of resolvers) {
			this.resolvers[resolver.type] = resolver
			resolver.prepare(this.renderer)
		}
	}

	/* Starting and cancelling asset resolving requests */

	cleanup() {
		this.removeAllArtefacts()
		this.transientArtefactsByScope.clear()

		for (const name in this.resolvers) {
			this.resolvers[name]?.cleanup(this.renderer)
		}
	}

	async resolve(photo: Photo = this.renderer.photo, type: AssetRequestType, id: string) {
		return this.resolvers[type]?.resolve(id, {
			renderer: this.renderer,
			photo: photo,
		})
	}

	async resolveAll(photo: Photo) {
		const missingArtefacts = this.getUnresolvedArtefactsForAdjustments(photo.adjustments)

		if (missingArtefacts.length === 0) {
			return 0
		}

		console.log(`[assets:${this.renderer.label}] Artefacts required:`, missingArtefacts)

		const loadingAssets: Promise<void>[] = []

		for (const missingArtefact of missingArtefacts) {
			console.log(
				`[assets:${this.renderer.label}] Resolving asset`,
				missingArtefact.type,
				missingArtefact.id,
			)

			const asset = this.renderer.assets.resolve(
				photo,
				missingArtefact.type,
				missingArtefact.id,
			)

			if (asset instanceof Promise) {
				loadingAssets.push(asset)
			}
		}

		return Promise.allSettled(loadingAssets)
	}

	/* Get all assets that need to be resolved for adjustments */

	// Checks whether we need to resolve artefacts
	shouldResolveArtefactsForAdjustments(adjustments: Adjustments): boolean {
		const missingArtefacts = this.getUnresolvedArtefactsForAdjustments(adjustments)

		return missingArtefacts.length > 0
	}

	// Get list of artefacts that need to be resolved
	getUnresolvedArtefactsForAdjustments(adjustments: Adjustments): ArtefactId[] {
		const allArtefactIds = this.getRequiredArtefactsForAdjustments(adjustments)

		return allArtefactIds.filter(
			(artefactId) => typeof getRegistryArtefact(this.artefacts, artefactId) === "undefined",
		)
	}

	// Collects all artefacts for adjustments that are necessary for rendering
	getRequiredArtefactsForAdjustments(adjustments: Adjustments): ArtefactId[] {
		const requiredArtefacts: ArtefactId[] = []

		for (const localAdjustment of adjustments.localAdjustments ?? []) {
			for (const mask of localAdjustment.masks) {
				if (
					mask.type === "subject" ||
					mask.type === "background" ||
					mask.type === "sky" ||
					mask.type === "people"
				) {
					requiredArtefacts.push({ type: "ai-mask", id: mask.id })
				}
			}
		}

		if (adjustments.userLutId) {
			requiredArtefacts.push({ type: "lut", id: adjustments.userLutId })
		}

		// Add healing AI sources (with cache ID that includes variation + settings)
		for (const healing of adjustments.healingMasks ?? []) {
			if (healing.aiVariations && healing.aiVariations.length > 0) {
				const cacheId = getHealingRenderDataId(healing)
				requiredArtefacts.push({ type: "healing-ai", id: cacheId })
			}
		}

		return requiredArtefacts
	}

	/* Managing asset artefacts that exist in renderer realm */

	setArtefact(key: ArtefactId, value: any) {
		setRegistryArtefact(this.artefacts, key, value)
	}

	getArtefact(key: ArtefactId): any {
		const regularArtefact = getRegistryArtefact(this.artefacts, key)
		if (typeof regularArtefact !== "undefined") {
			return regularArtefact
		}

		const transientScopes = [...this.transientArtefactsByScope.values()]
		for (let index = transientScopes.length - 1; index >= 0; index -= 1) {
			const scope = transientScopes[index]
			if (!scope) {
				continue
			}
			const transientArtefact = getRegistryArtefact(scope, key)
			if (typeof transientArtefact !== "undefined") {
				return transientArtefact
			}
		}
		return
	}

	removeArtefact(key: ArtefactId) {
		const typedArtefacts = this.artefacts.get(key.type)
		typedArtefacts?.delete(key.id)
		if (typedArtefacts?.size === 0) {
			this.artefacts.delete(key.type)
		}
	}

	removeAllArtefacts() {
		this.artefacts.clear()
	}

	setTransientAssets(scopeId: string, assets: ArtefactEntries) {
		const replacement: ArtefactRegistry = new Map()
		for (const [key, value] of assets) {
			setRegistryArtefact(replacement, key, value)
		}
		const scopes = new Map(this.transientArtefactsByScope)
		// Reinsert a replaced scope so overlapping transient keys consistently prefer the latest set.
		scopes.delete(scopeId)
		scopes.set(scopeId, replacement)
		this.transientArtefactsByScope = scopes
	}

	clearTransientAssets(scopeId: string) {
		if (!this.transientArtefactsByScope.has(scopeId)) {
			return
		}
		const scopes = new Map(this.transientArtefactsByScope)
		scopes.delete(scopeId)
		this.transientArtefactsByScope = scopes
	}
}

export interface AssetResolverContext {
	photo: Photo
	renderer: Renderer
}

export abstract class AssetResolver {
	public abstract get type(): AssetRequestType

	public async resolve(_id: string, _context: AssetResolverContext): Promise<any> {}

	public prepare(_renderer: Renderer) {}

	public cleanup(_renderer?: Renderer) {}
}
